import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { MATCH_SABOTAGE_CARDS } from '@solante/shared';
import { ClientMatchState } from '../../state/roomStore';

type SacrificeType = 'scoreBoost' | 'drawCards' | 'cleanse';

interface Props {
  state?: ClientMatchState;
  selectedCardId?: string | null;
  onSelect: (cardId: string | null) => void;
  isMyTurn: boolean;
  onSacrifice?: (type: SacrificeType) => void;
}

const sabotageIndex = new Map(MATCH_SABOTAGE_CARDS.map((card) => [card.id, card]));
const TEMPO_CAP = 3;

const SACRIFICE_OPTIONS: Array<{
  type: SacrificeType;
  label: string;
  icon: string;
  description: string;
}> = [
  { type: 'scoreBoost', label: '+50% Score', icon: '💥', description: 'Boost this turn' },
  { type: 'drawCards', label: '+3 Cards', icon: '🃏', description: 'Draw extra cards' },
  { type: 'cleanse', label: 'Cleanse', icon: '✨', description: 'Remove debuffs' },
];

export const SabotagePanel: React.FC<Props> = ({
  state,
  selectedCardId,
  onSelect,
  isMyTurn,
  onSacrifice,
}) => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const myCards = useMemo(() => {
    if (!wallet) return [];
    return state?.playerPowers?.[wallet] ?? [];
  }, [state?.playerPowers, wallet]);

  const myDebuff = wallet ? state?.pendingDebuffs?.[wallet] : undefined;

  const tempoState = state?.tempoState ?? {};
  const myTempo = wallet ? (tempoState[wallet] ?? 0) : 0;
  const opponentTempos = useMemo(() => {
    if (!wallet) return [] as Array<[string, number]>;
    return Object.entries(tempoState).filter(([key]) => key !== wallet) as Array<[string, number]>;
  }, [tempoState, wallet]);

  const activeAnomaly = state?.activeAnomaly ?? null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col h-full rounded-2xl border border-white/10 bg-black/40 p-2 text-sm text-white/80 overflow-hidden"
    >
      {/* Compact header */}
      <header className="flex items-center justify-between mb-1.5 shrink-0">
        <span className="font-arcade text-[9px] uppercase tracking-wide text-neon-500">POWERS</span>
        <div className="flex items-center gap-1.5">
          {myDebuff?.shieldCharges && (
            <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[8px] text-emerald-300">
              🛡{myDebuff.shieldCharges}
            </span>
          )}
          {myDebuff?.scorePenalty && (
            <span className="rounded bg-rose-500/20 px-1.5 py-0.5 text-[8px] text-rose-300">
              -{myDebuff.scorePenalty}
            </span>
          )}
          {activeAnomaly && (
            <span className="rounded bg-purple-500/20 px-1.5 py-0.5 text-[8px] text-purple-300">
              ⚡
            </span>
          )}
        </div>
      </header>

      {/* Tempo bars - compact */}
      <div className="flex items-center gap-2 mb-1.5 shrink-0">
        <div className="flex-1">
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full rounded-full bg-neon-500"
              initial={false}
              animate={{ width: `${Math.min(myTempo / TEMPO_CAP, 1) * 100}%` }}
            />
          </div>
          <span className="text-[8px] text-white/40">
            YOU {myTempo}/{TEMPO_CAP}
          </span>
        </div>
        {opponentTempos.length > 0 && (
          <div className="flex-1">
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                className="h-full rounded-full bg-red-400"
                initial={false}
                animate={{ width: `${Math.min(opponentTempos[0][1] / TEMPO_CAP, 1) * 100}%` }}
              />
            </div>
            <span className="text-[8px] text-white/40">
              OPP {opponentTempos[0][1]}/{TEMPO_CAP}
            </span>
          </div>
        )}
      </div>

      {/* Power cards - scrollable */}
      <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
        {wallet ? (
          myCards.length > 0 ? (
            myCards.map((cardId) => {
              const card = sabotageIndex.get(cardId);
              if (!card) return null;
              const queued = selectedCardId === cardId;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => onSelect(queued ? null : card.id)}
                  disabled={!isMyTurn}
                  className={`w-full rounded-lg border px-2 py-1.5 text-left transition disabled:opacity-50 ${
                    queued
                      ? 'border-emerald-400/40 bg-emerald-500/20'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-white">{card.name}</span>
                    <span
                      className={`text-[8px] uppercase ${queued ? 'text-emerald-400' : 'text-white/40'}`}
                    >
                      {queued ? '✓' : card.rarity}
                    </span>
                  </div>
                </button>
              );
            })
          ) : (
            <div className="flex items-center justify-center h-full text-[9px] text-white/40">
              No powers yet
            </div>
          )
        ) : (
          <div className="flex items-center justify-center h-full text-[9px] text-white/40">
            Connect wallet
          </div>
        )}
      </div>

      {/* Queued indicator */}
      {selectedCardId && (
        <div className="mt-1 pt-1 border-t border-white/5 shrink-0">
          <span className="text-[9px] text-emerald-300">
            ⚔ {sabotageIndex.get(selectedCardId)?.name ?? 'Power'} queued
          </span>
        </div>
      )}

      {/* Sacrifice section */}
      {onSacrifice && wallet && (
        <div className="mt-2 pt-2 border-t border-white/10 shrink-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-arcade text-[9px] uppercase tracking-wide text-rose-400">
              SACRIFICE
            </span>
            <span className="text-[8px] text-white/40">Cost: 1 ❤️</span>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {SACRIFICE_OPTIONS.map((option) => {
              const myRunState = state?.playerRunStates?.[wallet];
              const hasEnoughHearts = (myRunState?.hearts ?? 0) >= 1;
              return (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => onSacrifice(option.type)}
                  disabled={!isMyTurn || !hasEnoughHearts}
                  title={option.description}
                  className={`rounded-lg border px-1.5 py-1 text-center transition disabled:opacity-40 ${
                    hasEnoughHearts && isMyTurn
                      ? 'border-rose-400/40 bg-rose-500/20 hover:bg-rose-500/30 text-rose-200'
                      : 'border-white/10 bg-white/5 text-white/40'
                  }`}
                >
                  <div className="text-sm">{option.icon}</div>
                  <div className="text-[7px] font-semibold truncate">{option.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </motion.section>
  );
};
